import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';
import { LayoutSplashScreen } from '../helpers/MetronicSplashScreen';
import { AuthModel, UserModel } from '../auth/_models';
import * as authHelper from '../helpers/AuthHelpers';
import { getUserByToken, IPIFY_API_URL, logout, saveUserIpAddress } from './_requests';
import { WithChildren } from '../helpers/react18MigrationHelpers';
import { useDescope } from '@descope/react-sdk';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { logThirdPartyError } from '../helpers/utils/safeSentryError';
import { revokeWebPushOnDeviceForUser } from '../helpers/webPush/webPushClient';
import {
  SIGNUP_PAY_SETUP_HANDLED_KEY,
  START_PAY_RENT_SETUP_KEY,
} from '../helpers/utils/signupConsent';
import { clearAnalyticsUser, setAnalyticsUser } from '../helpers/utils/analytics';
import { pushStorage } from '../helpers/webPush/pushNotificationStorage';
import { COMPLETE_BUILDING_MODAL_KEY } from '../helpers/hooks/modalContext';

type AuthContextProps = {
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: UserModel | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
  logout: () => void;
};

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>();
  const { logoutAll } = useDescope();

  // Attach the logged-in user to every Sentry event (errors and Nuvei request logs).
  useEffect(() => {
    const user = currentUser?.user;
    Sentry.setUser(
      user
        ? {
            id: String(user.userId),
            email: user.email,
            username: user.username,
            name: [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
          }
        : null
    );
  }, [currentUser]);
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth);
    if (auth) {
      authHelper.setAuth(auth);
    } else {
      authHelper.removeAuth();
    }
  };

  const clearAuthState = () => {
    if (currentUser?.user?.userId != null) {
      pushStorage.clearDismissedThisSession(currentUser.user.userId);
    }
    saveAuth(undefined);
    setCurrentUser(undefined);
    localStorage.removeItem('isSignUp');
    localStorage.removeItem(START_PAY_RENT_SETUP_KEY);
    localStorage.removeItem(COMPLETE_BUILDING_MODAL_KEY);
    sessionStorage.removeItem(SIGNUP_PAY_SETUP_HANDLED_KEY);
    document.cookie = 'rememberMe=false; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    clearAnalyticsUser();
    logoutAll();
  };

  const handleLogout = () => {
    void revokeWebPushOnDeviceForUser(currentUser?.user?.userId);
    void logout()
      .catch((error) => {
        console.error('Logout API failed:', error);
      })
      .finally(() => {
        clearAuthState();
      });
  };

  return (
    <AuthContext.Provider
      value={{ auth, saveAuth, currentUser, setCurrentUser, logout: handleLogout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, logout, currentUser, setCurrentUser } = useAuth();
  const didRequest = useRef(false);
  const ipAddressSent = useRef(false);
  const [showSplashScreen, setShowSplashScreen] = useState(true);

  // Send IP address once when app starts for logged-in users
  useEffect(() => {
    const sendIpAddress = async (apiToken: string) => {
      // Only send once per app session
      if (ipAddressSent.current || !apiToken) return;

      try {
        // Get IP address from ipify API
        const ipResponse = await axios.get(IPIFY_API_URL);
        const ipAddress = ipResponse.data.ip;

        // Save IP address to backend
        await saveUserIpAddress(apiToken, ipAddress);
        ipAddressSent.current = true;
      } catch (error) {
        // Log error to Sentry for IP address fetching
        logThirdPartyError(new Error('Error fetching or saving user IP address on app start'), {
          api: 'ipify',
          endpoint: IPIFY_API_URL,
          error: error instanceof Error ? error.message : String(error),
        });
        // Don't block app initialization if IP address fails
        console.error('Failed to send IP address:', error);
      }
    };

    if (auth && auth.data.token) {
      sendIpAddress(auth.data.token);
    }
  }, [auth?.data.token]);

  useEffect(() => {
    setAnalyticsUser(currentUser?.user?.userId, undefined, auth?.data.tenantId);
  }, [currentUser?.user?.userId, auth?.data.tenantId]);

  // We should request user by authToken (auth.data.token) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          const { data } = await getUserByToken(apiToken);
          if (data) {
            // Identify synchronously: children (and their first page_view) mount as soon as
            // the splash hides, before the effect below would run.
            setAnalyticsUser(data.user?.userId, undefined, auth?.data.tenantId);
            setCurrentUser(data);
          }
        }
      } catch (error) {
        console.error(error);
        if (!didRequest.current) {
          logout();
        }
      } finally {
        setShowSplashScreen(false);
      }

      return () => (didRequest.current = true);
    };

    if (auth && auth.data.token) {
      requestUser(auth.data.token);
    } else {
      logout();
      setShowSplashScreen(false);
    }
  }, []);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>;
};

export { AuthProvider, AuthInit, useAuth };
